export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
};

export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; Max-Age=-1; path=/;`;
};

export const clearMsalTokens = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.includes('msal') || key.includes('login.windows.net')) {
      localStorage.removeItem(key);
    }
  });

  Object.keys(sessionStorage).forEach((key) => {
    if (key.includes('msal') || key.includes('login.windows.net')) {
      sessionStorage.removeItem(key);
    }
  });
};

export const hashToken = async (token: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(token);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

export const isEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key) || !isEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

export const getTodayDate = () => {
  const today = new Date();

  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = String(today.getFullYear()).slice(-2);

  return `${day}-${month}-${year}`;
};

export const formatNumber = (num: number, fixed = 1) => {
  if (num === undefined) return '0';
  num = parseFloat(num.toString());
  const isNegative = num < 0;
  num = Math.abs(num);

  if (num < Math.pow(10, -fixed)) {
    return isNegative ? `-0.${'0'.repeat(fixed)}` : `0.${'0'.repeat(fixed)}`;
  }

  if (num < 1000) {
    const formattedNum = num.toFixed(fixed);
    return isNegative ? `-${formattedNum}` : formattedNum;
  }

  const suffixes = ['', 'K', 'M', 'B', 'T'];
  let index = 0;

  while (num >= 1000 && index < suffixes.length - 1) {
    num /= 1000.0;
    index++;
  }

  const formattedNum = num.toFixed(fixed) + suffixes[index];

  if (isNaN(Number(num.toFixed(fixed)))) return '0.0';

  return isNegative ? `-${formattedNum}` : formattedNum;
};

export const formatToNumber = (num: number, decimalPlaces: number): number => {
  if (decimalPlaces === 0) {
    return Math.floor(num);
  }

  const factor = Math.pow(10, decimalPlaces);

  return Math.floor(num * factor) / factor;
};

export const convertKeysToLowerCaseAndUnderscore = (arr: Array<any>): Array<any> => {
  return arr.map((obj) => {
    const transformedObj: { [key: string]: any } = {};

    Object.keys(obj).forEach((key) => {
      const newKey = key.toLowerCase().replace(/\s+/g, '_');

      if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        transformedObj[newKey] = convertKeysToLowerCaseAndUnderscore([obj[key]])[0];
      } else if (Array.isArray(obj[key])) {
        transformedObj[newKey] = convertKeysToLowerCaseAndUnderscore(obj[key]);
      } else {
        transformedObj[newKey] = obj[key];
      }
    });

    return transformedObj;
  });
};

export const formatCustomDate = (date: Date | null): string => {
  if (!date || isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const truncateName = (name: any, val: any) => {
  return name.length > val ? name.substring(0, val) + '...' : name;
};

export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as T;
  }

  if (Array.isArray(obj)) {
    const arrayCopy: any[] = [];
    for (const element of obj) {
      arrayCopy.push(deepClone(element));
    }
    return arrayCopy as unknown as T;
  }

  if (obj instanceof Object) {
    const objCopy: { [key: string]: any } = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        objCopy[key] = deepClone(obj[key]);
      }
    }
    return objCopy as T;
  }

  throw new Error('Unsupported data type');
}

export const calculateRange = (minValue: number, maxValue: number): number[] => {
  const ranges: number[] = [];
  if (minValue >= 0) {
    const intervalWidth = (maxValue - minValue) / 2;
    ranges.push(0, 0, 0);
    for (let i = 1; i < 3; i++) {
      ranges.push(minValue + i * intervalWidth);
    }
  } else if (maxValue <= 0) {
    const intervalWidth = (minValue - maxValue) / 2;
    for (let i = 0; i < 3; i++) {
      ranges.push(minValue + i * intervalWidth);
    }
    ranges.push(0, 0, 0);
  } else {
    const negRange = Math.abs(minValue) / 2;
    const posRange = maxValue / 2;
    ranges.push(minValue, -negRange, 0, posRange, maxValue);
  }

  return ranges;
};

export const updateArrayByDifference = (array1: string[], array2: string[]): string[] => {
  const set1 = new Set(array1);
  return array2.filter((item) => !set1.has(item));
};

export const isInvalidColumn = (row: any, columnId: string) => {
  return Array.isArray(row.Invalid_Column) && row.Invalid_Column.includes(columnId);
};

export const formatName = (name: string) => {
  const nameParts = name.split(' ');
  const firstName = nameParts[0];
  const lastNameInitial = nameParts[1]?.charAt(0);

  return `${firstName} ${lastNameInitial ? `${lastNameInitial}.` : ''}`;
};

export const validNumber = (value: any) => {
  return value !== null && value !== undefined ? value : '';
};

export const validString = (value: any) => {
  return value && value.includes(' ') ? `"${value}"` : value;
};

export const convertRGMDataToCSV = (data: any, base: string, child: string) => {
  const scenarios = Object.keys(data);
  const rows = [];

  const headers = [
    '"Scenario"',
    '"Segment"',
    '"Pack Size"',
    '"Volume UC"',
    '"Volume Uc Growth (%)"',
    '"Volume PC"',
    '"Volume PC Growth (%)"',
    '"Transaction"',
    '"Transaction Growth (%)"',
    '"Net Revenue"',
    '"Net Revenue Growth (%)"',
    '"NR/UC"',
    '"NR/UC Growth (%)"',
  ];
  rows.push(headers.join(','));

  const validNumber = (value: any) => {
    return value !== null && value !== undefined ? value : '';
  };

  const validString = (value: any) => {
    return value && value.includes(' ') ? `"${value}"` : value;
  };

  scenarios.forEach((scenario) => {
    data[scenario].forEach((segmentData: any) => {
      const baseRow = [
        scenario === 'scenario_1' ? base : child,
        validString(segmentData.segment),
        'total',
        validNumber(segmentData.volume_uc),
        validNumber(segmentData.volume_uc_growth),
        validNumber(segmentData.volume_pc),
        validNumber(segmentData.volume_pc_growth),
        validNumber(segmentData.transaction),
        validNumber(segmentData.transaction_growth),
        validNumber(segmentData.net_revenue),
        validNumber(segmentData.net_revenue_growth),
        validNumber(segmentData.nr_by_uc),
        validNumber(segmentData.nr_by_uc_growth),
      ];
      rows.push(baseRow.join(','));

      segmentData.pack.forEach((packData: any) => {
        const packRow = [
          scenario === 'scenario_1' ? base : child,
          validString(packData.segment),
          validString(packData.pack_size),
          validNumber(packData.volume_uc),
          validNumber(packData.volume_uc_growth),
          validNumber(packData.volume_pc),
          validNumber(packData.volume_pc_growth),
          validNumber(packData.transaction),
          validNumber(packData.transaction_growth),
          validNumber(packData.net_revenue),
          validNumber(packData.net_revenue_growth),
          validNumber(packData.nr_by_uc),
          validNumber(packData.nr_by_uc_growth),
        ];
        rows.push(packRow.join(','));
      });
    });
  });
  return rows.join('\n');
};

export const convertFinanceDataToCSV = (data: any, base: string, child: string) => {
  const scenarios = Object.keys(data);
  const rows = [];

  const headers = [
    '"Scenario"',
    '"Segment"',
    '"Pack Size"',
    '"Bottler GP"',
    '"Bottler GP Growth (%)"',
    '"Bottler GP (%)"',
    '"OU GP"',
    '"OU GP_Growth (%)"',
    '"OU GP (%)"',
    '"System GP"',
    '"System GP Growth (%)"',
    '"System GP (%)"',
  ];

  rows.push(headers.join(','));

  scenarios.forEach((scenario) => {
    data[scenario].forEach((segmentData: any) => {
      const baseRow = [
        scenario === 'scenario_1' ? base : child,
        validString(segmentData.segment),
        'total',
        validNumber(segmentData.bottler_gp),
        validNumber(segmentData.bottler_gp_growth),
        validNumber(segmentData.bottler_gp_percent),
        validNumber(segmentData.ou_gp),
        validNumber(segmentData.ou_gp_growth),
        validNumber(segmentData.ou_gp_percent),
        validNumber(segmentData.system_gp),
        validNumber(segmentData.system_gp_growth),
        validNumber(segmentData.system_gp_percent),
      ];
      rows.push(baseRow.join(','));

      segmentData.pack.forEach((packData: any) => {
        const packRow = [
          scenario === 'scenario_1' ? base : child,
          validString(packData.segment),
          validString(packData.pack_size),
          validNumber(packData.bottler_gp),
          validNumber(packData.bottler_gp_growth),
          validNumber(packData.bottler_gp_percent),
          validNumber(packData.ou_gp),
          validNumber(packData.ou_gp_growth),
          validNumber(packData.ou_gp_percent),
          validNumber(packData.system_gp),
          validNumber(packData.system_gp_growth),
          validNumber(packData.system_gp_percent),
        ];
        rows.push(packRow.join(','));
      });
    });
  });
  return rows.join('\n');
};

export const downloadCSV = (csvData: string, filename = 'segment_deepdive.csv') => {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

export const getDropdownStyles = (color: string) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: `${color} !important`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${color} !important`,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${color} !important`,
  },
  '& .MuiInputBase-root': {
    border: `1px solid ${color} !important`,
    borderRadius: '3.81px',
  },
});

export const getinputPropsStyles = (color: string) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: `${color} !important`,
    border: `1px solid ${color} !important`,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${color} !important`,
    border: `1px solid ${color} !important`,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: `${color} !important`,
    border: `1px solid ${color} !important`,
  },
});
